import { CustomFieldType } from './custom-field';
import { fileKeyToDownloadUrl } from './file';
import { safeParse } from './js-helpers/json';
import { isNonEmptyString } from './js-helpers/str';

/**
 * Resolves a stored file key to a public download URL when it is not already absolute.
 * Used when reading `customFields` / custom form JSON straight from the DB (no TypeORM @AfterLoad).
 */
export function resolveStorageKeyToDownloadUrlIfNeeded(
  fileKey: string,
  downloadBaseUrl: string
): string {
  if (!fileKey || typeof fileKey !== 'string') {
    return fileKey;
  }
  if (fileKey.startsWith('http://') || fileKey.startsWith('https://')) {
    return fileKey;
  }
  return fileKeyToDownloadUrl(downloadBaseUrl, fileKey);
}

/**
 * Enriches `doc` and `multidoc` custom field values from storage keys to full download URLs.
 */
export function enrichCustomFieldDocAndMultidocValues<T extends { type?: string; value?: unknown }>(
  field: T,
  downloadBaseUrl: string
): T {
  try {
    if (field.type === CustomFieldType.Document && isNonEmptyString(field.value)) {
      return {
        ...field,
        value: resolveStorageKeyToDownloadUrlIfNeeded(field.value, downloadBaseUrl)
      };
    }
    if (field.type === CustomFieldType.MultiDocument && field.value) {
      let fileKeys: unknown = field.value;
      if (typeof field.value === 'string') {
        const parsed = safeParse<unknown>(field.value);
        if (parsed === null) {
          // safeParse returns null on error and when JSON is the literal `null`.
          fileKeys = field.value.trim() === 'null' ? null : [];
        } else {
          fileKeys = parsed;
        }
      }
      if (Array.isArray(fileKeys)) {
        const validKeys = fileKeys.filter(key => key && typeof key === 'string');
        return {
          ...field,
          value: validKeys.map(k =>
            resolveStorageKeyToDownloadUrlIfNeeded(k as string, downloadBaseUrl)
          )
        };
      }
    }
  } catch {
    return field;
  }
  return field;
}

export function mapCustomFieldsWithDocDownloadUrls<T extends { type?: string; value?: unknown }>(
  fields: unknown,
  downloadBaseUrl: string
): T[] {
  if (!Array.isArray(fields)) {
    return [];
  }
  return fields.map(field => enrichCustomFieldDocAndMultidocValues(field, downloadBaseUrl));
}
